import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Calendar, Package, Image, Sun, LogOut, 
  Menu, X, User, FileText, Briefcase, MessageSquare, Users
} from 'lucide-react';
import './AdminLayout.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const adminUser = localStorage.getItem('adminUser');
    
    if (!token) {
      navigate('/admin/login');
      return;
    }

    if (adminUser) {
      setUser(JSON.parse(adminUser));
    }

    fetchStats();
  }, [navigate]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  const menuItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/bookings', icon: Calendar, label: 'Bookings', badge: stats?.pending_bookings },
    { path: '/admin/services', icon: Package, label: 'Services' },
    { path: '/admin/portfolio', icon: Image, label: 'Portfolio' },
    { path: '/admin/holidays', icon: Sun, label: 'Holidays' },
    { path: '/admin/blogs', icon: FileText, label: 'Blog' },
    { path: '/admin/careers', icon: Briefcase, label: 'Careers' },
    { path: '/admin/applications', icon: Users, label: 'Applications' },
    { path: '/admin/reviews', icon: MessageSquare, label: 'Reviews' },
  ];

  return (
    <div className={`admin-layout ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <Link to="/admin/dashboard" className="sidebar-logo">
            <span className="logo-text">RSE</span>
            <span className="logo-subtitle">Admin</span>
          </Link>
          <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${isActive ? 'active' : ''}`}
              >
                <Icon size={20} />
                <span className="nav-label">{item.label}</span>
                {item.badge > 0 && <span className="nav-badge">{item.badge}</span>}
              </Link>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <button className="logout-btn" onClick={handleLogout}>
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="admin-main">
        <header className="admin-header">
          <button className="mobile-menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <Menu size={24} />
          </button>
          
          <div className="header-right">
            <Link to="/" className="view-site-btn" target="_blank">
              View Site
            </Link>
            <div className="admin-user">
              <User size={20} />
              <span>{user?.name || 'Admin'}</span>
            </div>
          </div>
        </header>

        <main className="admin-content">
          <Outlet context={{ stats, fetchStats }} />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
